import { useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import {
    FaArrowRight,
    FaCog,
    FaFileInvoiceDollar,
    FaFolderOpen,
    FaStar,
    FaTrash,
} from 'react-icons/fa';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTenant } from '../context/TenantContext';
import { usePermissions } from '../context/PermissionsContext';
import { PERMISSIONS } from '../constants/permissions';
import { apiFetchBillingSheets } from '../utils/billingSetupApi';
import BillingBreadcrumbs from '../components/BillingBreadcrumbs';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

const TABS = [
    { id: 'open', label: 'Open period' },
    { id: 'worksheets', label: 'Worksheets' },
    { id: 'sheets', label: 'Sheet configs' },
];

function getHistory(tenantId) {
    try {
        return JSON.parse(localStorage.getItem(`billing_history_${tenantId}`) || '[]');
    } catch {
        return [];
    }
}

function buildMonthStrip() {
    const result = [];
    for (let offset = -5; offset <= 2; offset++) {
        let m = currentMonth + offset;
        let y = currentYear;
        if (m <= 0) { m += 12; y -= 1; }
        if (m > 12) { m -= 12; y += 1; }
        result.push({ month: m, year: y });
    }
    return result;
}

const MONTH_STRIP = buildMonthStrip();

function buildBillingUrl(siteId, year, month, sheetId) {
    const monthStr = String(month).padStart(2, '0');
    const base = `/billing/${siteId}/${year}/${monthStr}`;
    if (!sheetId || sheetId === 'default') return base;
    return `${base}?sheet=${encodeURIComponent(sheetId)}`;
}

function worksheetKey(h) {
    return `${h.siteId}|${h.year}|${h.month}|${h.sheetId || ''}`;
}

function Billing() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentTenant, sites, fetchSites, isLoadingSites } = useTenant();
    const { hasPermission } = usePermissions();
    const canCreateSheet = hasPermission(PERMISSIONS.BILLING_SHEET_CREATE);
    const canUpdateSheet = hasPermission(PERMISSIONS.BILLING_SHEET_UPDATE);
    const canConfig = hasPermission(PERMISSIONS.BILLING_CONFIG_MANAGE);
    const canRemoveWorksheet = hasPermission(PERMISSIONS.BILLING_WORKSHEET_REMOVE);
    const canManageSheets = canCreateSheet || canUpdateSheet || canConfig;

    const tabFromUrl = searchParams.get('tab');
    const initialTab = TABS.some(t => t.id === tabFromUrl) ? tabFromUrl : 'open';
    const [activeTab, setActiveTab] = useState(initialTab);
    const [selectedSite, setSelectedSite] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedSheet, setSelectedSheet] = useState('default');
    const [billingSheets, setBillingSheets] = useState([]);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        const next = searchParams.get('tab');
        if (TABS.some(t => t.id === next) && next !== activeTab) {
            setActiveTab(next);
        }
    }, [searchParams]);

    useEffect(() => {
        if (currentTenant?.id) {
            fetchSites(currentTenant.id);
        }
    }, [currentTenant?.id, fetchSites]);

    useEffect(() => {
        if (!currentTenant?.id) {
            setBillingSheets([]);
            return;
        }
        apiFetchBillingSheets(currentTenant.id)
            .then(sheets => {
                setBillingSheets(sheets);
                const def = sheets.find(s => s.is_default);
                setSelectedSheet(def ? def.id : (sheets[0]?.id || ''));
            })
            .catch(() => {
                setBillingSheets([]);
                setSelectedSheet('');
            });
    }, [currentTenant?.id]);

    const refreshHistory = () => {
        if (currentTenant?.id) setHistory(getHistory(currentTenant.id));
        else setHistory([]);
    };

    const setTab = (tabId) => {
        setActiveTab(tabId);
        const next = new URLSearchParams(searchParams);
        if (tabId === 'open') next.delete('tab');
        else next.set('tab', tabId);
        setSearchParams(next, { replace: true });
        if (tabId === 'worksheets') refreshHistory();
    };

    useEffect(() => {
        refreshHistory();
    }, [currentTenant?.id]);

    // Refresh worksheet list when returning to this page / tab
    useEffect(() => {
        const onFocus = () => refreshHistory();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [currentTenant?.id]);

    const handleOpen = () => {
        if (!selectedSite || !selectedSheet) return;
        navigate(buildBillingUrl(selectedSite, selectedYear, selectedMonth, selectedSheet));
    };

    const isMonthSelected = (m, y) => m === selectedMonth && y === selectedYear;

    const selectedSiteName = sites.find(s => String(s.siteId) === String(selectedSite))?.siteName || '';

    const selectedSheetLabel =
        billingSheets.find(s => String(s.id) === String(selectedSheet))?.name || 'Select a sheet';

    const resolveHistorySheetId = (h) => {
        if (h.sheetId && h.sheetId !== 'all' && billingSheets.some(s => String(s.id) === String(h.sheetId))) {
            return h.sheetId;
        }
        const def = billingSheets.find(s => s.is_default);
        return def?.id || billingSheets[0]?.id || '';
    };

    const openWorksheet = (h) => {
        const sid = resolveHistorySheetId(h);
        if (!sid) return;
        navigate(buildBillingUrl(h.siteId, h.year, h.month, sid));
    };

    const removeHistoryEntry = (target) => {
        if (!currentTenant?.id) return;
        const key = `billing_history_${currentTenant.id}`;
        const next = history.filter(h => worksheetKey(h) !== worksheetKey(target));
        localStorage.setItem(key, JSON.stringify(next));
        setHistory(next);
    };

    const clearHistorySection = (draftsOnly) => {
        if (!currentTenant?.id) return;
        const key = `billing_history_${currentTenant.id}`;
        // draftsOnly true → keep saved; false → keep drafts
        const kept = draftsOnly
            ? history.filter(h => h.isDraft === false)
            : history.filter(h => h.isDraft !== false);
        localStorage.setItem(key, JSON.stringify(kept));
        setHistory(kept);
    };

    const drafts = useMemo(
        () => history.filter(h => h.isDraft !== false).sort((a, b) => String(b.lastAccessed).localeCompare(String(a.lastAccessed))),
        [history]
    );
    const saved = useMemo(
        () => history.filter(h => h.isDraft === false).sort((a, b) => String(b.lastAccessed).localeCompare(String(a.lastAccessed))),
        [history]
    );

    const renderWorksheetCard = (h) => (
        <div
            key={worksheetKey(h)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '14px 16px',
                backgroundColor: 'var(--theme-surface)',
                border: '1px solid var(--theme-border)',
                borderRadius: '8px',
            }}
        >
            <button
                type="button"
                onClick={() => openWorksheet(h)}
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    textAlign: 'left',
                    cursor: 'pointer',
                    minWidth: 0,
                }}
            >
                <div style={{
                    width: '36px', height: '36px', borderRadius: '8px',
                    backgroundColor: 'var(--theme-surface-muted)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <FaFileInvoiceDollar style={{ color: 'var(--theme-text-secondary)' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                    <div style={{
                        fontSize: '0.875rem', fontWeight: 600, color: 'var(--theme-text)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {h.siteName || h.siteId}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>
                        {MONTHS[Number(h.month) - 1]} {h.year}
                        {h.sheetName && <span style={{ color: 'var(--theme-text-muted)' }}> · {h.sheetName}</span>}
                    </div>
                    {h.lastAccessed && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-muted)', marginTop: '2px' }}>
                            {new Date(h.lastAccessed).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                        </div>
                    )}
                </div>
            </button>
            {canRemoveWorksheet && (
                <button
                    type="button"
                    title="Remove from list"
                    onClick={() => removeHistoryEntry(h)}
                    style={{
                        border: 'none', background: 'transparent', color: 'var(--theme-text-muted)',
                        cursor: 'pointer', padding: '6px',
                    }}
                >
                    <FaTrash style={{ fontSize: '0.8rem' }} />
                </button>
            )}
        </div>
    );

    return (
        <div style={{ padding: '28px 32px', minHeight: '100%', backgroundColor: 'var(--brand-cream, #fafaf7)' }}>

            <div className="mb-4">
                <BillingBreadcrumbs items={[
                    { label: 'Billing' },
                    ...(activeTab !== 'open' ? [{ label: TABS.find(t => t.id === activeTab)?.label || activeTab }] : []),
                ]} />
                <h2 style={{ margin: 0, fontWeight: 700, color: 'var(--theme-text)' }}>Billing</h2>
                <p style={{ color: 'var(--theme-text-secondary)', marginTop: '6px', marginBottom: 0, fontSize: '0.95rem' }}>
                    Open a period worksheet, manage drafts and saved worksheets, or review sheet configurations.
                </p>
            </div>

            <div style={{
                display: 'flex', gap: '4px', marginBottom: '24px',
                borderBottom: '1px solid var(--theme-border)',
            }}>
                {TABS.map(tab => {
                    const active = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setTab(tab.id)}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                padding: '10px 16px',
                                fontSize: '0.9rem',
                                fontWeight: active ? 650 : 500,
                                color: active ? 'var(--theme-text)' : 'var(--theme-text-muted)',
                                borderBottom: active ? '2px solid var(--theme-text)' : '2px solid transparent',
                                marginBottom: '-1px',
                                cursor: 'pointer',
                            }}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {activeTab === 'open' && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '300px 1fr',
                    gap: '32px',
                    alignItems: 'start',
                }}>
                    <div style={{
                        backgroundColor: 'var(--theme-surface)',
                        borderRadius: '10px',
                        border: '1px solid var(--theme-border)',
                        padding: '24px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    }}>
                        <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
                            Billing Sheet
                        </p>
                        {billingSheets.length === 0 ? (
                            <div style={{
                                marginBottom: '16px', fontSize: '0.875rem', color: 'var(--theme-text-secondary)',
                                backgroundColor: 'var(--theme-surface-muted)', borderRadius: '6px', padding: '12px 14px',
                            }}>
                                No billing sheets configured yet.
                                {canCreateSheet ? (
                                    <>
                                        {' '}
                                        <Link to="/billing-sheets" style={{ fontWeight: 600, color: 'var(--theme-text)' }}>
                                            Create a billing sheet
                                        </Link>
                                        {' '}to choose groups, care types, and GL placements.
                                    </>
                                ) : (
                                    ' Ask a billing manager to configure one.'
                                )}
                            </div>
                        ) : (
                            <Form.Select
                                value={selectedSheet}
                                onChange={e => setSelectedSheet(e.target.value)}
                                style={{ marginBottom: '16px', fontSize: '0.95rem' }}
                            >
                                {billingSheets.map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}{s.is_default ? ' (default)' : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        )}

                        <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
                            Site
                        </p>
                        <Form.Select
                            value={selectedSite}
                            onChange={e => setSelectedSite(e.target.value)}
                            disabled={isLoadingSites}
                            style={{ marginBottom: '20px', fontSize: '0.95rem' }}
                        >
                            <option value="">
                                {isLoadingSites ? 'Loading…' : 'Select a site…'}
                            </option>
                            {sites.map(s => (
                                <option key={s.siteId} value={s.siteId}>{s.siteName}</option>
                            ))}
                        </Form.Select>

                        <div style={{
                            backgroundColor: 'var(--theme-surface-muted)',
                            borderRadius: '6px',
                            padding: '12px 14px',
                            marginBottom: '20px',
                            fontSize: '0.875rem',
                            color: selectedSite ? 'var(--theme-text)' : 'var(--theme-text-muted)',
                            borderLeft: `3px solid ${selectedSite ? 'var(--theme-text)' : 'var(--theme-border)'}`,
                        }}>
                            {selectedSite
                                ? (
                                    <>
                                        <strong>{selectedSheetLabel}</strong><br />
                                        <strong>{selectedSiteName}</strong><br />
                                        {MONTHS[selectedMonth - 1]} {selectedYear}
                                    </>
                                )
                                : 'Choose a sheet, site, and period to continue'
                            }
                        </div>

                        <Button
                            variant="dark"
                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                            onClick={handleOpen}
                            disabled={!selectedSite || !selectedSheet}
                            style={{ fontWeight: 600 }}
                        >
                            Open worksheet
                            <FaArrowRight style={{ fontSize: '0.85rem' }} />
                        </Button>
                    </div>

                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                                Period
                            </p>
                            <div className="d-flex gap-2 align-items-center">
                                <Form.Select
                                    size="sm"
                                    value={selectedMonth}
                                    onChange={e => setSelectedMonth(Number(e.target.value))}
                                    style={{ width: '120px', fontSize: '0.82rem' }}
                                >
                                    {MONTHS.map((m, i) => (
                                        <option key={i} value={i + 1}>{m}</option>
                                    ))}
                                </Form.Select>
                                <Form.Select
                                    size="sm"
                                    value={selectedYear}
                                    onChange={e => setSelectedYear(Number(e.target.value))}
                                    style={{ width: '84px', fontSize: '0.82rem' }}
                                >
                                    {YEARS.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </Form.Select>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {MONTH_STRIP.map(({ month: m, year: y }) => {
                                const selected = isMonthSelected(m, y);
                                const isCurrent = m === currentMonth && y === currentYear;
                                const isFuture = y > currentYear || (y === currentYear && m > currentMonth);
                                return (
                                    <button
                                        key={`${y}-${m}`}
                                        type="button"
                                        onClick={() => { setSelectedMonth(m); setSelectedYear(y); }}
                                        style={{
                                            flex: '1 1 80px',
                                            padding: '14px 8px',
                                            border: selected ? '2px solid var(--theme-text)' : '1px solid var(--theme-border)',
                                            borderRadius: '8px',
                                            backgroundColor: selected ? 'var(--theme-text)' : isCurrent ? 'var(--theme-accent-soft)' : 'var(--theme-surface)',
                                            color: selected ? 'var(--theme-page-bg)' : isFuture ? 'var(--theme-text-muted)' : 'var(--theme-text-body)',
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                            transition: 'all 0.12s',
                                            boxShadow: selected ? '0 2px 8px var(--theme-shadow)' : '0 1px 2px rgba(0,0,0,0.04)',
                                        }}
                                    >
                                        <div style={{ fontSize: '1rem', fontWeight: 700 }}>{SHORT_MONTHS[m - 1]}</div>
                                        <div style={{ fontSize: '0.72rem', opacity: 0.75, marginTop: '2px' }}>{y}</div>
                                        {isCurrent && !selected && (
                                            <div style={{ fontSize: '0.6rem', color: '#16a34a', fontWeight: 600, marginTop: '3px' }}>
                                                CURRENT
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'worksheets' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '28px', alignItems: 'start' }}>
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                                Drafts ({drafts.length})
                            </p>
                            {drafts.length > 0 && canRemoveWorksheet && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm('Clear all drafts from this list? Worksheet data in the browser is not deleted.')) {
                                            clearHistorySection(true);
                                        }
                                    }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--theme-text-muted)', padding: 0 }}
                                >
                                    Clear drafts
                                </button>
                            )}
                        </div>
                        {drafts.length === 0 ? (
                            <div style={{
                                padding: '28px 18px', textAlign: 'center', color: 'var(--theme-text-muted)',
                                backgroundColor: 'var(--theme-surface)', border: '1px dashed var(--theme-border)', borderRadius: '8px',
                            }}>
                                <FaFolderOpen style={{ fontSize: '1.4rem', marginBottom: '8px', opacity: 0.5 }} />
                                <div style={{ fontSize: '0.85rem' }}>No drafts yet. Open a period and use Save Draft.</div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {drafts.map(renderWorksheetCard)}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                                Saved ({saved.length})
                            </p>
                            {saved.length > 0 && canRemoveWorksheet && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm('Clear all saved worksheets from this list? Worksheet data in the browser is not deleted.')) {
                                            clearHistorySection(false);
                                        }
                                    }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--theme-text-muted)', padding: 0 }}
                                >
                                    Clear saved
                                </button>
                            )}
                        </div>
                        {saved.length === 0 ? (
                            <div style={{
                                padding: '28px 18px', textAlign: 'center', color: 'var(--theme-text-muted)',
                                backgroundColor: 'var(--theme-surface)', border: '1px dashed var(--theme-border)', borderRadius: '8px',
                            }}>
                                <FaFileInvoiceDollar style={{ fontSize: '1.4rem', marginBottom: '8px', opacity: 0.5 }} />
                                <div style={{ fontSize: '0.85rem' }}>No saved worksheets yet. Open a period and click Save.</div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {saved.map(renderWorksheetCard)}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'sheets' && (
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                            Billing sheet configurations
                        </p>
                        {canManageSheets && (
                            <Link
                                to="/billing-sheets"
                                style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--theme-text)', textDecoration: 'none' }}
                            >
                                <FaCog style={{ marginRight: '6px' }} />
                                Manage sheets
                            </Link>
                        )}
                    </div>

                    {billingSheets.length === 0 ? (
                        <div style={{
                            padding: '40px 20px', textAlign: 'center', color: 'var(--theme-text-muted)',
                            backgroundColor: 'var(--theme-surface)', border: '1px dashed var(--theme-border)', borderRadius: '8px',
                        }}>
                            <p style={{ marginBottom: '12px' }}>No sheet configs yet.</p>
                            {canCreateSheet && (
                                <Link to="/billing-sheets" style={{ fontWeight: 600 }}>Create your first billing sheet</Link>
                            )}
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                            {billingSheets.map(s => (
                                <div
                                    key={s.id}
                                    style={{
                                        backgroundColor: 'var(--theme-surface)',
                                        border: '1px solid var(--theme-border)',
                                        borderRadius: '10px',
                                        padding: '16px 18px',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                        <div style={{ fontWeight: 650, color: 'var(--theme-text)', fontSize: '0.95rem' }}>
                                            {s.name}
                                        </div>
                                        {s.is_default && (
                                            <span title="Default sheet" style={{ color: '#d97706', fontSize: '0.8rem' }}>
                                                <FaStar />
                                            </span>
                                        )}
                                    </div>
                                    {s.description && (
                                        <div style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary)', marginBottom: '10px' }}>
                                            {s.description}
                                        </div>
                                    )}
                                    <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-muted)', marginBottom: '4px' }}>
                                        Groups: {(s.billing_group_ids || []).length || 0}
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-muted)', marginBottom: '4px' }}>
                                        Care types: {(s.customer_care_type_ids || []).length || 0}
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-muted)', marginBottom: '14px' }}>
                                        GL placements: {(s.gl_assignments || []).length || 0}
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="dark"
                                            onClick={() => {
                                                setSelectedSheet(s.id);
                                                setTab('open');
                                            }}
                                        >
                                            Use to open
                                        </Button>
                                        {canUpdateSheet && (
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => navigate('/billing-sheets')}
                                            >
                                                Edit
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default Billing;
